import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "abc_cart_v1";

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { items: {}, storeId: null };
    const parsed = JSON.parse(raw);
    return { items: parsed.items || {}, storeId: parsed.storeId || null };
  } catch (e) {
    return { items: {}, storeId: null };
  }
}

export function CartProvider({ children }) {
  // Lazy-init from localStorage to avoid race with persist effect.
  const initial = useRef(readStorage()).current;
  const [items, setItems] = useState(initial.items);
  const [storeId, setStoreId] = useState(initial.storeId);

  // Persist on every change.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ items, storeId }));
    } catch (e) {
      // ignore
    }
  }, [items, storeId]);

  const addItem = (item, store) => {
    if (store && store !== storeId && Object.keys(items).length > 0) {
      // Switching stores — replace cart with the new item
      setItems({ [item.id]: { ...item, qty: 1 } });
      setStoreId(store);
      return;
    }
    setItems((prev) => {
      const existing = prev[item.id];
      const qty = existing ? existing.qty + 1 : 1;
      return { ...prev, [item.id]: { ...item, qty } };
    });
    if (store && !storeId) setStoreId(store);
  };

  const decrementItem = (itemId) => {
    setItems((prev) => {
      const existing = prev[itemId];
      if (!existing) return prev;
      if (existing.qty <= 1) {
        const next = { ...prev };
        delete next[itemId];
        return next;
      }
      return { ...prev, [itemId]: { ...existing, qty: existing.qty - 1 } };
    });
  };

  const removeItem = (itemId) => {
    setItems((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  };

  const clearCart = () => {
    setItems({});
    setStoreId(null);
  };

  const { count, subtotal, lines } = useMemo(() => {
    const lines = Object.values(items);
    const count = lines.reduce((s, l) => s + l.qty, 0);
    const subtotal = lines.reduce((s, l) => s + l.qty * l.price, 0);
    return { lines, count, subtotal };
  }, [items]);

  const value = {
    items,
    lines,
    count,
    subtotal,
    storeId,
    setStoreId,
    addItem,
    decrementItem,
    removeItem,
    clearCart,
    getQty: (id) => items[id]?.qty || 0,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
